import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Light from "./headers/light";
import AnimationRevealPage from "helpers/AnimationRevealPage";
import Swal from "sweetalert2";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Footerr from "./footers/Footer";
import ScrollToTop from "./ScrollToTop";

const CheckoutForm = () => {
  const { t, i18n } = useTranslation();
  const token = localStorage.getItem("authToken");
  const id = localStorage.getItem("id");
  const role = localStorage.getItem("role");
  const navigate = useNavigate();

  // --- LOGIQUE FIDÉLITÉ ---
  const userStr = localStorage.getItem("user");
  const userData = userStr ? JSON.parse(userStr) : null;
  const customId = userData ? userData.custom_id : null;

  const [cart, setCart] = useState([]);
  const [codePromo, setCodePromo] = useState("");
  const [appliedDiscountType, setAppliedDiscountType] = useState(null); // 'loyalty_points' | 'promo' | 'client_fidele_auto'
  const [discountAmount, setDiscountAmount] = useState(0);
  const [promoDiscountRate, setPromoDiscountRate] = useState(10); // 5% influenceur, 10% autres
  const [loyaltyPoints, setLoyaltyPoints] = useState(0); 
  const [message, setMessage] = useState("");
  
  const [customer, setCustomer] = useState({
    Name: "",
    Email: "",
    Phone: "",
    Address: "",
    City: "",
    notes: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 1. Calcul du Sous-Total
  const calculateSubtotal = () => {
    if (!cart || cart.length === 0) return 0;

    let subtotal = 0;

    const shampooItems = cart.filter(item => 
      item.productName && item.productName.toLowerCase().includes("shampoing")
    );
    const shampooQuantity = shampooItems.reduce((sum, item) => sum + parseInt(item.quantity || 1, 10), 0);

    if (shampooQuantity === 1) subtotal += 119;
    else if (shampooQuantity === 2) subtotal += 219;
    else if (shampooQuantity === 3) subtotal += 319;
    else if (shampooQuantity >= 4) subtotal += 399;

    const otherItems = cart.filter(item => 
      !item.productName || !item.productName.toLowerCase().includes("shampoing")
    );
    
    otherItems.forEach(item => {
      const price = parseFloat(item.productPrice || 229);
      const qty = parseInt(item.quantity || 1, 10);
      subtotal += price * qty;
    });

    return subtotal;
  };

  // 2. Charger le Panier initial & Informations du client
  useEffect(() => {
    const storedCart = JSON.parse(localStorage.getItem("cart")) || [];
    setCart(storedCart);

    if (customId) {
      axios.get(`https://rehabwmg.online/api/loyalty/${customId}`)
        .then(res => {
          const pointsActifs = res.data.points || res.data.points_actifs || 0;
          setLoyaltyPoints(pointsActifs);
          
          if (role === "client_fidele") {
            setCodePromo(customId);
          }
        })
        .catch(err => console.error("Erreur chargement points fidélité:", err));
    }
  }, [customId, role]);

  // 3. Recalculer automatiquement la remise lors des changements de panier / de rôle / de discount
  useEffect(() => {
    const currentSubtotal = calculateSubtotal();

    if (appliedDiscountType === "loyalty_points") {
      // 🟢 Si l'utilisateur a cliqué sur le bouton de fidélité :
      // Il bénéficie de la réduction de ses points
      const pointsDH = loyaltyPoints * 10;
      
      // Option A: Si le client fidèle conserve AUSSI ses -10% automatiques + ses points:
      const autoDiscount = role === "client_fidele" ? currentSubtotal * 0.10 : 0;
      setDiscountAmount(parseFloat((pointsDH + autoDiscount).toFixed(2)));

    } else if (appliedDiscountType === "promo") {
      // Code promo : 5% pour un influenceur, 10% pour les autres fournisseurs
      const calculatedDiscount = currentSubtotal * (promoDiscountRate / 100);
      setDiscountAmount(parseFloat(calculatedDiscount.toFixed(2)));

    } else if (role === "client_fidele") {
      // Remise Automatique Client Fidèle (-10%)
      const autoDiscount = currentSubtotal * 0.10;
      setDiscountAmount(parseFloat(autoDiscount.toFixed(2)));
      setAppliedDiscountType("client_fidele_auto");
    }
  }, [cart, role, appliedDiscountType, loyaltyPoints, promoDiscountRate]);

  // Calcul du Total Final Net
  const getFinalTotal = () => {
    const subtotal = calculateSubtotal();
    const final = subtotal - discountAmount;
    return final > 0 ? parseFloat(final.toFixed(2)) : 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomer((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuantityChange = (productID, newQuantity) => {
    if (newQuantity < 1) return;
    const updatedCart = cart.map((item) =>
      item.productID === productID ? { ...item, quantity: newQuantity } : item
    );
    setCart(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
  };

  const handleRemoveItem = (productID) => {
    const updatedCart = cart.filter((item) => item.productID !== productID);
    setCart(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
  };

  // 🔒 LOGIQUE DU CLIC SUR LE BOUTON "APPLIQUER LE CODE PROMO / FIDÉLITÉ"
  const handlePromoCodeApply = async () => {
    const trimmedCode = codePromo.trim().toUpperCase();

    if (!trimmedCode) {
      setMessage(t("Entrer_code_promo"));
      return;
    }

    try {
      const isFideliteCode = trimmedCode.startsWith("CF") || trimmedCode === (customId ? customId.toUpperCase() : "");

      if (isFideliteCode) {
        const loyaltyRes = await axios.get(`https://rehabwmg.online/api/loyalty/${trimmedCode}`);
        const pointsActifs = loyaltyRes.data.points || loyaltyRes.data.points_actifs || 0;
        const valeurDH = loyaltyRes.data.valeur_dh || (pointsActifs * 10);

        setLoyaltyPoints(pointsActifs);

        if (pointsActifs <= 0) {
          setMessage(`Ce code de fidélité n'a aucun point actif (0 DH).`);
          if (role === "client_fidele") {
            setAppliedDiscountType("client_fidele_auto");
          } else {
            setDiscountAmount(0);
            setAppliedDiscountType(null);
          }
          return;
        }

        // 🟢 Clic confirmé -> Active le mode 'loyalty_points'
        setAppliedDiscountType("loyalty_points");
        setMessage(`Code fidélité appliqué ! Remise de -${valeurDH}.00 DH (${pointsActifs} PTS)`);
        return;
      }

      // Code promo : 5% pour un influenceur, 10% pour les autres rôles
      const response = await axios.get(
        `https://rehabwmg.online/api/supplier-from-code/${trimmedCode}`
      );

      console.log("Réponse code promo :", response.data);

      // Le backend renvoie supplier_id et role au niveau principal.
      // Le fallback supplier?.role permet aussi de gérer l'ancien format de réponse.
      const supplierIdFromCode = response.data?.supplier_id;
      const supplierRole = (
        response.data?.role || response.data?.supplier?.role || ""
      ).toLowerCase();

      if (supplierIdFromCode) {
        const discountRate = supplierRole === "influenceur" ? 5 : 10;

        setPromoDiscountRate(discountRate);
        setAppliedDiscountType("promo");
        setMessage(`${t("Code_promo_succes")} (-${discountRate}%)`);
      } else {
        setMessage(t("Code_promo_invalide"));
        if (role === "client_fidele") {
          setAppliedDiscountType("client_fidele_auto");
        } else {
          setDiscountAmount(0);
          setAppliedDiscountType(null);
        }
      }

    } catch (error) {
      console.error("Erreur lors de la vérification du code:", error);
      setMessage(t("Code_promo_invalide"));
      if (role === "client_fidele") {
        setAppliedDiscountType("client_fidele_auto");
      } else {
        setDiscountAmount(0);
        setAppliedDiscountType(null);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!/^0[5-7][0-9]{8}$/.test(customer.Phone)) {
      Swal.fire("Erreur", "Numéro de téléphone invalide.", "error");
      setIsSubmitting(false);
      return;
    }

    const sanitizedNote = (customer.notes || '').trim().slice(0, 120);
    let supplierId = id;
    
    const isPointsApplied = appliedDiscountType === 'loyalty_points';
    let paymentType = isPointsApplied ? 'point' : 'COD';
    let finalPromoCode = isPointsApplied ? codePromo.trim() : '';

    if (appliedDiscountType === 'promo') {
      finalPromoCode = codePromo.trim();
      try {
        const response = await axios.get(`https://rehabwmg.online/api/supplier-from-code/${codePromo.trim()}`);
        supplierId = response.data.supplier_id || response.data.id;
      } catch (error) {
        console.error("Erreur supplier code promo");
      }
    }

    try {
      const customerResponse = await axios.post(
        "https://rehabwmg.online/api/customers/create",
        { ...customer, notes: sanitizedNote, ...(supplierId && { SupplierID: supplierId }) },
        { headers: { "Content-Type": "application/json", ...(token && { Authorization: `Bearer ${token}` }) } }
      );

      const customerId = customerResponse.data.customer.CustomerID;

      const shampooItems = cart.filter(item => item.productName && item.productName.toLowerCase().includes("shampoing"));
      const totalShampooQty = shampooItems.reduce((sum, item) => sum + parseInt(item.quantity || 1, 10), 0);
      
      let totalShampooPrice = 0;
      if (totalShampooQty === 1) totalShampooPrice = 119;
      else if (totalShampooQty === 2) totalShampooPrice = 219;
      else if (totalShampooQty === 3) totalShampooPrice = 319;
      else if (totalShampooQty >= 4) totalShampooPrice = 399;

      const shampooPricePerUnit = totalShampooQty > 0 ? parseFloat((totalShampooPrice / totalShampooQty).toFixed(2)) : 119;
      const finalTotalWithReduction = getFinalTotal();

      const orderData = {
        product_ids: cart.map((item) => item.productID),
        quantities: cart.map((item) => parseInt(item.quantity || 1, 10)), 
        notes: sanitizedNote,
        customer_id: customerId,
        promo_code: finalPromoCode,
        use_points: isPointsApplied,
        supplierId: supplierId || id,
        total: finalTotalWithReduction,
        payment_type: paymentType,
        discount_amount: discountAmount,
        prices: cart.map((item) => {
          if (item.productName && item.productName.toLowerCase().includes("shampoing")) {
            return shampooPricePerUnit;
          }
          return parseFloat(item.productPrice || 229);
        }),
        isOffer: cart.map((item) => {
          if (item.productName && item.productName.toLowerCase().includes("shampoing")) {
            return totalShampooQty > 1;
          }
          return false;
        }),
      };

      await axios.post("https://rehabwmg.online/api/orders/create", orderData, {
        headers: { "Content-Type": "application/json", ...(token && { Authorization: `Bearer ${token}` }) }
      });

      Swal.fire(t("Commande_reussie"), t("Commande_succes"), "success");
      localStorage.removeItem("cart");
      navigate("/");
    } catch (error) {
      setIsSubmitting(false);
      Swal.fire("Erreur!", "Problème lors de la création.", "error");
    }
  };

  return (
    <>
      <Helmet>
        <html lang={i18n.language} />
        <title>Finaliser ma commande | Rehab Bio</title>
      </Helmet>

      <AnimationRevealPage>
        <ScrollToTop />
        <Light />
        <div className="container" style={{ marginTop: "40px", marginBottom: "40px" }}>
          <div className="row">
            {/* GAUCHE : Panier et Formulaire */}
            <div className="col-xl-8">
              <div className="card border shadow-none mb-4">
                <div className="card-body">
                  {cart.length > 0 ? (
                    <>
                      <ul className="list-group list-group-flush">
                        {cart.map((item, index) => (
                          <li className="list-group-item py-3" key={index}>
                            <div className="d-flex align-items-center flex-wrap">
                              <img 
                                src={`https://rehabwmg.online/storage/${item.productImage}`} 
                                alt="" 
                                className="rounded me-3" 
                                style={{ width: "60px", height: "60px", objectFit: "cover" }} 
                              />
                              
                              <div className="flex-grow-1 min-width-0 me-3">
                                <h6 className="mb-0 text-truncate">{item.productName}</h6>
                                <small className="text-muted">{(parseFloat(item.productPrice || 119)).toFixed(2)} DH</small>
                              </div>

                              <div className="d-flex align-items-center me-4">
                                <button
                                  type="button"
                                  className="btn btn-sm d-flex align-items-center justify-content-center p-0"
                                  style={{
                                    width: "28px",
                                    height: "28px",
                                    backgroundColor: "#4e7662",
                                    color: "#ffffff",
                                    borderRadius: "6px",
                                    border: "none",
                                    opacity: item.quantity <= 1 ? 0.4 : 1,
                                    transition: "all 0.2s ease"
                                  }}
                                  onClick={() => handleQuantityChange(item.productID, item.quantity - 1)}
                                  disabled={item.quantity <= 1}
                                >
                                  <i className="fa fa-minus" style={{ fontSize: "10px" }}></i>
                                </button>
                                
                                <span className="mx-3 fw-bold" style={{ color: "#2d3748", minWidth: "16px", textAlign: "center" }}>
                                  {item.quantity}
                                </span>
                                
                                <button
                                  type="button"
                                  className="btn btn-sm d-flex align-items-center justify-content-center p-0"
                                  style={{
                                    width: "28px",
                                    height: "28px",
                                    backgroundColor: "#4e7662",
                                    color: "#ffffff",
                                    borderRadius: "6px",
                                    border: "none",
                                    transition: "all 0.2s ease"
                                  }}
                                  onClick={() => handleQuantityChange(item.productID, item.quantity + 1)}
                                >
                                  <i className="fa fa-plus" style={{ fontSize: "10px" }}></i>
                                </button>
                              </div>
                              
                              <div className="text-end me-3" style={{ minWidth: "90px" }}>
                                <span className="fw-bold">
                                  {(parseFloat(item.productPrice || 119) * item.quantity).toFixed(2)} DH
                                </span>
                              </div>

                              <div className="text-end">
                                <button
                                  type="button"
                                  className="btn p-2 d-inline-flex align-items-center justify-content-center"
                                  style={{
                                    backgroundColor: "transparent",
                                    color: "#4e7662",
                                    border: "none",
                                    boxShadow: "none",
                                    transition: "color 0.2s ease"
                                  }}
                                  onClick={() => handleRemoveItem(item.productID)}
                                  title={t("Supprimer") || "Supprimer"}
                                  onMouseEnter={(e) => e.currentTarget.style.color = "#dc3545"}
                                  onMouseLeave={(e) => e.currentTarget.style.color = "#4e7662"}
                                >
                                  <i className="fa fa-trash-alt" style={{ fontSize: "18px" }}></i>
                                </button>
                              </div>                    
                            </div>
                          </li>
                        ))}
                      </ul>

                      <h5 className="mt-4 mb-3">{t("Informations_client")}</h5>
                      <form onSubmit={handleSubmit}>
                        <div className="mb-3"><input type="text" name="Name" placeholder={t("Nom")} className="form-control" onChange={handleInputChange} required /></div>
                        <div className="mb-3"><input type="text" name="Phone" placeholder={t("Numéro_de_Téléphone")} className="form-control" onChange={handleInputChange} required /></div>
                        <div className="mb-3"><input type="text" name="Address" placeholder={t("Adresse")} className="form-control" onChange={handleInputChange} required /></div>
                        <div className="mb-3"><input type="text" name="City" placeholder={t("Ville")} className="form-control" onChange={handleInputChange} required /></div>
                        <div className="mb-3"><textarea name="notes" placeholder={t('note')} className="form-control" rows={3} maxLength={120} value={customer.notes} onChange={handleInputChange} /></div>
                         
                        <button type="submit" className="btn w-100" disabled={isSubmitting} style={{ backgroundColor: "#4e7662", color: "white", fontWeight: "bold" }}>
                          {isSubmitting ? t("En_cours") + "..." : t("Confirmer_commande")}
                        </button>
                      </form>
                    </>
                  ) : (
                    <p className="text-center">{t("Panier_vide")}.</p>
                  )}
                </div>
              </div>
            </div>

            {/* DROITE : Résumé de la commande */}
            <div className="col-xl-4">
              <div className="card border shadow-none">
                <div className="card-header bg-transparent border-bottom py-3">
                  <h5 className="font-size-16 mb-0 text-center">{t("Resume_commande")}</h5>
                </div>
                <div className="card-body">
                  
                  {role === "client_fidele" && (
                    <div className="alert alert-success p-2 small mb-3">
                      <i className="fa fa-gift me-2"></i>
                      <strong>Statut Client Fidèle :</strong> Remise de 10% appliquée !
                      <br />
                      <strong>Code Fidélité :</strong> {customId}
                      <br />
                      Points disponibles : <strong>{loyaltyPoints} PTS</strong> ({loyaltyPoints * 10} DH)
                    </div>
                  )}

                  <div className="mb-3">
                    <input 
                      type="text" 
                      placeholder={t("Code_promo")} 
                      className="form-control mb-2" 
                      value={codePromo}
                      onChange={(e) => setCodePromo(e.target.value)} 
                    />
                    <button 
                      type="button" 
                      onClick={handlePromoCodeApply} 
                      className="btn btn-sm w-100" 
                      style={{ backgroundColor: "#4e7662", color: "#fff" }}
                    >
                      {t("Appliquer_code_promo")}
                    </button>
                    {message && <p className="small mt-2 text-center text-primary fw-bold">{message}</p>}
                  </div>

                  <table className="table table-borderless mb-0">
                    <tbody>
                      <tr>
                        <td>{t("Sous_total")}</td>
                        <td className="text-end">{calculateSubtotal().toFixed(2)} DH</td>
                      </tr>

                      {/* Remise Automatique Client Fidèle (-10%) */}
                      {appliedDiscountType === "client_fidele_auto" && (
                        <tr>
                          <td className="text-success fw-bold">Remise Client Fidèle (-10%)</td>
                          <td className="text-end text-success fw-bold">-{discountAmount.toFixed(2)} DH</td>
                        </tr>
                      )}

                      {/* Remise Points Fidélité (+ Remise -10% si Client Fidèle) */}
                      {appliedDiscountType === "loyalty_points" && (
                        <>
                          {role === "client_fidele" && (
                            <tr>
                              <td className="text-success fw-bold">Remise Client Fidèle (-10%)</td>
                              <td className="text-end text-success fw-bold">
                                -{(calculateSubtotal() * 0.10).toFixed(2)} DH
                              </td>
                            </tr>
                          )}
                          <tr>
                            <td className="text-success fw-bold">Remise Points Fidélité ({loyaltyPoints} PTS)</td>
                            <td className="text-end text-success fw-bold">
                              -{(loyaltyPoints * 10).toFixed(2)} DH
                            </td>
                          </tr>
                        </>
                      )}

                      {/* Remise Code Promo Partner : 5% influenceur, 10% autres */}
                      {appliedDiscountType === "promo" && (
                        <tr>
                          <td className="text-primary fw-bold">Remise Code Promo (-{promoDiscountRate}%)</td>
                          <td className="text-end text-primary fw-bold">-{discountAmount.toFixed(2)} DH</td>
                        </tr>
                      )}

                      <tr className="border-top">
                        <th className="pt-2">{t("Total")}</th>
                        <th className="text-end pt-2" style={{ color: "#4e7662", fontSize: "1.2rem" }}>
                          {getFinalTotal().toFixed(2)} DH
                        </th>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footerr />
      </AnimationRevealPage>
    </>
  );
};

export default CheckoutForm;